// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { typeof Formik } from 'formik';
import { Map, withLeaflet } from 'react-leaflet';
import { bindActionCreators } from 'redux';
import { connectAPI } from 'speed-js-react';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import { get, isEmpty } from 'lodash';
import { HTTP_400_BAD_REQUEST } from 'speed-js-core';
import { withRouter } from 'react-router';
import type { Match } from 'react-router-dom';
import { getSidePanel } from 'esm-web-pro/redux/side-panel';
import type { SidePanel } from 'esm-web-pro/redux/side-panel/reducer';
import * as sidePanelActionCreators from 'esm-web-pro/redux/side-panel/actions';
import Creator from 'esm-web-pro/components/maps/Creator';
import MapObjectForm from 'esm-web-pro/containers/map/forms/MapObjectForm';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import { COVERAGE_EDIT_KEY } from 'esm-web-pro/containers/pages/coverage/Util';
import type { Resource } from '../../map/types';


type Props = {
    context: { uuid: string, identity: string },
    updateObject: (resourceName: string, map: string, data: Object) => Promise<Object>,
    map: { current: null | typeof Map },
    onClose: Function,
    editor: BaseEditor,
    leaflet: Object,
    resource: Resource | typeof undefined,
    match: Match,
    handleUpdateObject: (data: Object) => void,
    object: Object,
    sidePanel: ?SidePanel,
    history: RouterHistory,
    setSensitivity: (key: string, sensitivity: boolean) => void,
}

type State = {
    canSave: boolean,
    layer: Object
}

const mapStateToProps = (state) => ({
    sidePanel: getSidePanel(state, COVERAGE_EDIT_KEY),
});

@withRouter
@withLeaflet
@connectAPI((apiClient: ApiClient) => ({
    updateObject: {
        send: (resourceName, map, objectId, data) => {
            const resource = get(apiClient.resources, resourceName);
            return resource.update(map, objectId, data);
        },
    },
}))
@connect(mapStateToProps, (dispatch) => (bindActionCreators(sidePanelActionCreators, dispatch)))
class CoverageEditor extends React.Component<Props, State> {
    formikRef: React.Ref<Formik>;

    resetLayer: boolean;

    static defaultProps = {
        leaflet: {},
        match: {},
        updateObject: () => {},
        map: {},
        sidePanel: null,
    };

    constructor(props: Props) {
        super(props);
        this.formikRef = React.createRef();
        this.state = { canSave: false, layer: null };
        this.resetLayer = true;
    }

    componentDidMount() {
        this.getLayer();
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const { sidePanel } = this.props;
        const { layer } = this.state;

        this.getLayer(prevState.layer);

        if (layer) {
            if (sidePanel) {
                if (!layer.pm.enabled()) {
                    layer.pm.enable({
                        allowSelfIntersection: false,
                    });
                }
            } else {
                layer.pm.disable();
            }
        }

        if (prevProps.sidePanel && !sidePanel) {
            if (this.resetLayer) {
                const { object } = this.props;
                layer.setLatLngs(object.polygon.coordinates[0].map((c) => [c[1], c[0]]));
            }
        }

        if (sidePanel && !prevProps.sidePanel && !this.resetLayer) {
            this.resetLayer = true;
        }
    }

    componentWillUnmount() {
        this.handleClose();
    }

    getLayer = (prevLayer = null) => {
        const { leaflet, match: { params: { objectIdentity } } } = this.props;
        leaflet.map.eachLayer((layer) => {
            if (layer.feature && layer.feature.properties.identity === objectIdentity
                && (!prevLayer || prevLayer.feature.id !== layer.feature.id)) {
                if (prevLayer) {
                    prevLayer.remove();
                }
                this.setState({ layer });
            }
        });
    };

    handleSave = () => {
        const {
            updateObject, context, resource, handleUpdateObject, history, match, setSensitivity,
        } = this.props;

        const { layer } = this.state;

        const drawingData = { polygon: layer.toGeoJSON().geometry };
        const formData = this.formikRef.current.getFormikBag().values;

        this.formikRef.current.setTouched(Object.keys(formData).reduce((touched, k) => {
            touched[k] = true;
            return touched;
        }, {}));

        const urlIdentity = match.params.objectIdentity;
        updateObject(resource.name, context.uuid, urlIdentity,
            { ...layer.feature.properties, ...formData, ...drawingData }).then((response) => {
            if (response.status === HTTP_400_BAD_REQUEST) {
                this.formikRef.current.setErrors(response.data);
            } else {
                this.resetLayer = false;

                handleUpdateObject(response);
                this.handleClose(true);
            }
        });
    };

    handleFormChange = () => {
        if (this.formikRef.current) {
            this.formikRef.current.getFormikBag().validateForm().then(() => {
                this.setState({ canSave: isEmpty(this.formikRef.current.getFormikBag().errors) });
            });
        } else {
            this.setState({ canSave: false });
        }
    };

    handleClose = (forceClose: boolean = false) => {
        const { onClose, editor } = this.props;
        onClose(forceClose);
        editor._loadFeatures();
    };


    render() {
        const { resource, object } = this.props;
        const { canSave, layer } = this.state;

        return layer && resource ? (
            <Creator
                id={COVERAGE_EDIT_KEY}
                title={(
                    <FormattedMessage
                        id="coverageeditor-title"
                        defaultMessage="Edit Coverage"
                    />
                )}
                onClose={this.handleClose}
                onSave={this.handleSave}
                isDrawing
                canSave={canSave}
                closeAllOpened={false}
                formikRef={this.formikRef}
            >
                <MapObjectForm
                    key={`${resource.name}-${layer.feature.id}`}
                    resourceName={resource.name}
                    resourceArguments={resource.arguments}
                    forwardRef={this.formikRef}
                    excludeFields={[resource.geometryField]}
                    initial={{
                        ...object,
                        [resource.geometryField]:
                        layer.toGeoJSON().geometry,
                    }}
                    objectId={layer.feature.id}
                    onChange={this.handleFormChange}
                    onInitialize={this.handleFormChange}
                />
            </Creator>
        ) : null;
    }
}

export default CoverageEditor;
